// ArtWorkARModalFixedView.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Button, Slider, Row, Col, Spin } from "antd";
import { ReloadOutlined, LoadingOutlined } from "@ant-design/icons";

const ArtWorkARModal = ({ visible, onCancel, artworkUrl, artworkWidth, artworkHeight }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const artImgRef = useRef(new Image());

  const [centerPoint, setCenterPoint] = useState(null);
  const [opacity, setOpacity] = useState(0.95);
  const [scale, setScale] = useState(1);
  const [cameraActive, setCameraActive] = useState(false);
  const [artImgLoaded, setArtImgLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  // Load artwork image
  useEffect(() => {
    if (!artworkUrl) return;
    setLoading(true);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = artworkUrl + "?_=" + Date.now();
    img.onload = () => {
      artImgRef.current = img;
      setArtImgLoaded(true);
      setLoading(false);
    };
    img.onerror = (err) => {
      console.error("Error loading artwork:", err);
      setLoading(false);
    };
  }, [artworkUrl]);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
        },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err) {
      console.error("Camera access error:", err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (visible) startCamera();
    else stopCamera();
    return stopCamera;
  }, [visible, startCamera, stopCamera]);

  // Place artwork
  const handleTap = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvasRef.current.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvasRef.current.height;
    setCenterPoint({ x, y });
  };

  // Parallax effect
  useEffect(() => {
    const handleMotion = (e) => {
      if (!e.gamma || !e.beta) return;
      setParallax({ x: e.gamma / 60, y: e.beta / 60 });
    };
    window.addEventListener("deviceorientation", handleMotion, true);
    return () => window.removeEventListener("deviceorientation", handleMotion);
  }, []);

  // Main draw loop
  useEffect(() => {
    if (!cameraActive || !canvasRef.current || !videoRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) {
        requestAnimationFrame(draw);
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      const { clientWidth, clientHeight } = canvas;
      canvas.width = clientWidth * dpr;
      canvas.height = clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Draw camera first
      ctx.drawImage(video, 0, 0, clientWidth, clientHeight);

      // Draw artwork next (on top)
      if (centerPoint && artImgLoaded) {
        const art = artImgRef.current;
        const aspect = (artworkWidth && artworkHeight)
          ? artworkWidth / artworkHeight
          : art.naturalWidth / art.naturalHeight;

        let artW = clientWidth * 0.6;
        let artH = artW / aspect;

        if (artH > clientHeight * 0.6) {
          artH = clientHeight * 0.6;
          artW = artH * aspect;
        }

        artW *= scale;
        artH *= scale;

        const x = centerPoint.x / dpr - artW / 2 + parallax.x * 20;
        const y = centerPoint.y / dpr - artH / 2 + parallax.y * 20;

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        ctx.shadowBlur = 20;
        ctx.drawImage(art, x, y, artW, artH);
        ctx.restore();
      }

      requestAnimationFrame(draw);
    };

    requestAnimationFrame(draw);
  }, [cameraActive, centerPoint, artImgLoaded, opacity, scale, parallax, artworkWidth, artworkHeight]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="100%"
      style={{ top: 0, padding: 0 }}
      bodyStyle={{
        padding: 0,
        height: "100vh",
        background: "#000",
        margin: 0,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        />
        <canvas
          ref={canvasRef}
          onClick={handleTap}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        />

        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(0,0,0,0.7)",
              zIndex: 3,
            }}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 36, color: "#fff" }} spin />}
              tip={<span style={{ color: "#fff" }}>Loading artwork...</span>}
            />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            background: "rgba(0,0,0,0.6)",
            padding: 10,
            textAlign: "center",
            zIndex: 4,
          }}
        >
          <h3 style={{ color: "#fff", margin: 0 }}>🎨 AR Artwork Preview</h3>
          <p style={{ color: "#ccc", marginBottom: 8 }}>Tap on wall to place artwork</p>

          <Row justify="center" gutter={10}>
            <Col>
              <label style={{ color: "#fff" }}>Opacity</label>
              <Slider
                min={0.3}
                max={1}
                step={0.01}
                value={opacity}
                onChange={setOpacity}
                style={{ width: 120 }}
              />
            </Col>
            <Col>
              <label style={{ color: "#fff" }}>Scale</label>
              <Slider
                min={0.5}
                max={2}
                step={0.05}
                value={scale}
                onChange={setScale}
                style={{ width: 120 }}
              />
            </Col>
          </Row>

          <div style={{ marginTop: 10 }}>
            <Button icon={<ReloadOutlined />} onClick={() => setCenterPoint(null)}>
              Reset
            </Button>{" "}
            <Button danger onClick={onCancel}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ArtWorkARModal;
